/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useContext, useMemo} from 'react'
import {GoogleTagManagerContext} from '../google-tag-manager/contexts'
import {BRAND_LABEL} from '../../brand'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {breakpoints} from '../../theme/_utils'

export const useGoogleTagManager = () => {
    const gtma = useContext(GoogleTagManagerContext)
    const config = gtma._config
    const id = config.id
    const {locale} = useMultiSite()
    const {data: customer} = useCurrentCustomer()

    const positionZeroEventName = 'startPageview'

    /**
     * Add layer
     *
     * @param {object} data - Arguments
     * @param {function} builder - Dedicated builder function for the arguments
     * @returns {object} - sent object
     */
    const addLayer = async (args, builder) => {
        if (id) {
            window.dataLayer = window.dataLayer || []
            await gtma.addLayer(builder ? builder({...args}) : args)
        }
    }

    /**
     *
     * Method to insert the page view with all the data always at position 0 of the dataLayer array
     * for backward compatibility with MCP (Marketing Cloud) which always reads from this position
     */
    const addLayerAtPositionZero = async (args, builder) => {
        if (id) {
            const data = builder ? await builder({...args}) : args

            window.dataLayer = window.dataLayer || []

            if (window.dataLayer?.[0]?.event === positionZeroEventName) {
                window.dataLayer[0] = data
            } else {
                await gtma.addLayer(builder ? builder({...args}) : args)

                const index = window.dataLayer.findIndex(
                    (item) => item.event === positionZeroEventName,
                )
                if (index > -1) {
                    const [gtmJsElement] = window.dataLayer.splice(index, 1)
                    window.dataLayer.unshift(gtmJsElement)
                }
            }

            /**
             * added new variable because, in the future, MCP will need to read the correct data from here and not from position zero
             */
            window._dataMCP = data
            return data
        }
    }

    const self = useMemo(
        () => ({
            ...config,
            addLayer: addLayer.bind(this, {
                locale,
                isGuest: customer.isGuest,
                customerId: customer.customerId,
                isRegistered: customer.isRegistered,
                brand: BRAND_LABEL,
            }),
            addLayerAtPositionZero: addLayerAtPositionZero.bind(this, {
                locale,
                isGuest: customer.isGuest,
                customerId: customer.customerId,
                isRegistered: customer.isRegistered,
                brand: BRAND_LABEL,
            }),
            gtma,
            id,
            positionZeroEventName,
        }),
        [customer],
    )

    return self
}

const md = parseInt(breakpoints.md)
const lg = parseInt(breakpoints.lg)

/**
 * Determines the device type based on the given width.
 *
 * @param {number} width - The width (in pixels) to evaluate.
 * @returns {'m' | 't' | 'd'} - Returns 'm' for mobile, 't' for tablet, and 'd' for desktop.
 */
export const windowGetDevice = (width) => {
    if (width < md) return 'm'
    else if (width < lg) return 't'
    else return 'd'
}
